// SPDX-License-Identifier: MIT
// The chat-first entry surface: a conversational thread with the INPUT PINNED
// AT THE TOP (operator brief 2026-06-21 — "laisser la fenêtre de chat en haut de
// page", users do several text exchanges on mobile before attaching anything).
// It is deliberately DUMB: `welcome.ts` owns the turn logic via `onTurn`
// (pre-magnify → IdeaDiscussion; post-magnify → converse) and `onMagnify`
// (package the transcript → magnify → fiche). Mirrors the proven project-chat.ts
// patterns (thread bubbles, "thinking" indicator, Enter-to-send).
import { t } from '../i18n/t';
import { escapeHtml } from '../lib/escape';
import type { Locale } from '../i18n/detect';

// One turn's outcome, reported by the host's onTurn. `ready` (pre-magnify) tells
// the chat to reveal the Magnifier CTA; `ficheVersion` (post-magnify) shows the
// "✓ fiche → vN" badge on the bot bubble when a converse turn evolved the sheet.
export interface ChatTurnResult {
  reply: string;
  ready?: boolean;
  ficheVersion?: number;
}

export interface DiscoveryChatOptions {
  locale: Locale;
  greeting: string;
  cap?: string; // override the cap line (default: t('discoveryChat.cap'))
  remaining?: number; // entitlement; <= 0 → Magnifier slot becomes a /tarifs CTA
  // 2026-07-02 incr.4a (§5.7): guided-run entrance cue on the Magnifier. A LAZY
  // getter — welcome.ts latches `guidedRun` AFTER the chat is created in the same
  // render pass, so a frozen boolean would always read false at creation time.
  guided?: () => boolean;
  onTurn(message: string): Promise<ChatTurnResult>;
  onMagnify(): Promise<void>;
}

export type DiscoveryChatElement = HTMLElement & {
  __appendBot?: (text: string, version?: number) => void;
  // Magnify-lifecycle hooks the host (welcome.ts) drives.
  __setBusy?: (busy: boolean) => void;
  __retireMagnify?: () => void;
  // 2026-06-23 guided first-run: programmatically run the first turn from the
  // deposit-box hand-off (« Commencer → »). Additive — same path as a typed send.
  __startWith?: (message: string) => void;
};

export function renderDiscoveryChat(opts: DiscoveryChatOptions): HTMLElement {
  const root = document.createElement('section') as DiscoveryChatElement;
  root.className = 'discovery-chat';
  root.setAttribute('data-component', 'discovery-chat');
  root.innerHTML = `
    <p class="discovery-chat__cap">${escapeHtml(opts.cap ?? t('discoveryChat.cap'))}</p>
    <div class="discovery-chat__bar">
      <input type="text" class="discovery-chat__input" data-component="discovery-input"
             placeholder="${escapeHtml(t('chat.placeholder'))}" autocomplete="off"
             aria-label="${escapeHtml(t('chat.placeholder'))}" />
      <button type="button" class="discovery-chat__send" data-component="discovery-send">${escapeHtml(t('chat.send'))}</button>
    </div>
    <div class="discovery-chat__cta" data-component="discovery-cta-slot"></div>
    <div class="discovery-chat__thread" data-component="discovery-thread" aria-live="polite"></div>
  `;

  const thread = root.querySelector<HTMLElement>('[data-component="discovery-thread"]')!;
  const input = root.querySelector<HTMLInputElement>('[data-component="discovery-input"]')!;
  const sendBtn = root.querySelector<HTMLButtonElement>('[data-component="discovery-send"]')!;
  const ctaSlot = root.querySelector<HTMLElement>('[data-component="discovery-cta-slot"]')!;

  const bubble = (role: 'user' | 'bot', text: string, version?: number): HTMLElement => {
    const el = document.createElement('div');
    el.className = `discovery-chat__msg discovery-chat__msg--${role}`;
    if (role === 'bot') {
      const badge =
        typeof version === 'number'
          ? `<span class="discovery-chat__upd" data-component="discovery-upd">${escapeHtml(
              t('chat.updated').replace('{n}', String(version)),
            )}</span>`
          : '';
      el.innerHTML = `<span class="discovery-chat__who">✦ Genesis</span>${escapeHtml(text)}${badge}`;
    } else {
      el.textContent = text;
    }
    return el;
  };
  const append = (role: 'user' | 'bot', text: string, version?: number): void => {
    thread.appendChild(bubble(role, text, version));
    thread.scrollTop = thread.scrollHeight;
  };

  // Opening line from Genesis.
  append('bot', opts.greeting);

  // Host hook: push a bot bubble after magnify (e.g. "Voilà ta fiche ✦").
  root.__appendBot = (text, version) => append('bot', text, version);
  // Magnify-lifecycle hooks (host-driven): __setBusy locks input + send + CTA
  // during magnify → save (so a follow-up can't race ahead of the saved project
  // id); __retireMagnify removes the spent Magnifier once the fiche is committed.
  root.__setBusy = (busy) => {
    sendBtn.disabled = busy;
    input.disabled = busy;
    const mag = ctaSlot.querySelector<HTMLButtonElement>('[data-component="discovery-magnify"]');
    if (mag) mag.disabled = busy;
  };
  root.__retireMagnify = () => ctaSlot.querySelector('[data-component="discovery-magnify"]')?.remove();

  const quotaExhausted = opts.remaining !== undefined && opts.remaining <= 0;

  const revealMagnify = (): void => {
    if (ctaSlot.querySelector('[data-component="discovery-magnify"]')) return;
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'discovery-chat__magnify';
    btn.setAttribute('data-component', 'discovery-magnify');
    btn.textContent = t('discoveryChat.magnify');
    btn.addEventListener('click', async () => {
      btn.disabled = true;
      try {
        await opts.onMagnify();
      } finally {
        btn.disabled = false;
      }
    });
    if (opts.guided?.()) btn.classList.add('discovery-chat__magnify--cue');
    ctaSlot.appendChild(btn);
  };

  if (quotaExhausted) {
    const cta = document.createElement('a');
    cta.href = '/tarifs';
    cta.setAttribute('data-component', 'discovery-quota-cta');
    cta.className = 'discovery-chat__quota-cta';
    cta.textContent = t('freemium.quotaExhausted');
    ctaSlot.appendChild(cta);
  }

  const submit = async (): Promise<void> => {
    const text = input.value.trim();
    if (!text || sendBtn.disabled) return;
    append('user', text);
    input.value = '';
    sendBtn.disabled = true;
    const thinking = document.createElement('div');
    thinking.className = 'discovery-chat__msg discovery-chat__msg--bot discovery-chat__msg--thinking';
    thinking.setAttribute('data-component', 'discovery-thinking');
    thinking.innerHTML = `${escapeHtml(t('chat.thinking'))}<span class="discovery-chat__dots"><i></i><i></i><i></i></span>`;
    thread.appendChild(thinking);
    thread.scrollTop = thread.scrollHeight;
    try {
      const turn = await opts.onTurn(text);
      thinking.remove();
      append('bot', turn.reply, turn.ficheVersion);
      if (turn.ready && !quotaExhausted) revealMagnify();
    } catch {
      thinking.remove();
      append('bot', t('chat.error'));
    } finally {
      sendBtn.disabled = false;
    }
  };

  sendBtn.addEventListener('click', () => void submit());
  input.addEventListener('keydown', (e) => {
    if ((e as KeyboardEvent).key === 'Enter') {
      e.preventDefault();
      void submit();
    }
  });

  // Hand-off from the deposit-box: run the first turn with the typed idea, exactly
  // as if the user had typed it and pressed send (reuses submit → onTurn).
  root.__startWith = (message: string): void => {
    const text = message.trim();
    if (!text) return;
    input.value = text;
    void submit();
  };

  return root;
}
